'use client';

import React, { useState, useCallback, useRef, useEffect } from 'react';

const RATE_PER_SQM = 360;

interface WindowEntry {
  id: number;
  width: string;
  height: string;
}

interface CalcResult {
  sqm: number;
  estimate: number;
}

export default function ReadyReckoner() {
  const [windows, setWindows] = useState<WindowEntry[]>([
    { id: 1, width: '', height: '' },
  ]);
  const [result, setResult] = useState<CalcResult | null>(null);
  const [error, setError] = useState('');

  const sectionRef = useRef<HTMLElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (leftRef.current) observer.observe(leftRef.current);
    if (rightRef.current) observer.observe(rightRef.current);
    return () => observer.disconnect();
  }, []);

  const addWindow = useCallback(() => {
    setWindows((prev) => [
      ...prev,
      { id: Date.now(), width: '', height: '' },
    ]);
  }, []);

  const removeWindow = useCallback((id: number) => {
    setWindows((prev) => prev.filter((w) => w.id !== id));
    setResult(null);
  }, []);

  const updateWindow = useCallback(
    (id: number, field: 'width' | 'height', value: string) => {
      setWindows((prev) =>
        prev.map((w) => (w.id === id ? { ...w, [field]: value } : w))
      );
      setResult(null);
    },
    []
  );

  const hasValues = windows.some((w) => w.width && w.height);

  const calculate = useCallback(() => {
    setError('');
    let totalSqm = 0;
    let valid = true;

    for (const w of windows) {
      const widthM = parseFloat(w.width);
      const heightM = parseFloat(w.height);
      if (!w.width || !w.height || isNaN(widthM) || isNaN(heightM) || widthM <= 0 || heightM <= 0) {
        valid = false;
        break;
      }
      totalSqm += widthM * heightM;
    }

    if (!valid) {
      setError('Please enter valid width and height (in metres) for all windows.');
      setResult(null);
      return;
    }

    const estimate = Math.round(totalSqm * RATE_PER_SQM);
    setResult({
      sqm: Math.round(totalSqm * 100) / 100,
      estimate,
    });
  }, [windows]);

  const reset = useCallback(() => {
    setWindows([{ id: 1, width: '', height: '' }]);
    setResult(null);
    setError('');
  }, []);

  return (
    <section
      ref={sectionRef}
      id="calculator"
      className="py-32 bg-[#FAF9F6]"
      aria-labelledby="calculator-heading"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left — info */}
          <div ref={leftRef} className="reveal-hidden-left">
            <p className="text-[#7B8A94] text-sm font-medium tracking-[0.25em] uppercase mb-4">
              Instant Guide Price
            </p>
            <h2
              id="calculator-heading"
              className="font-display text-[#0F1C2E] text-4xl lg:text-5xl font-medium leading-tight mb-6"
            >
              How Much Will My Shutters Cost?
            </h2>
            <p className="text-[#0F1C2E]/70 text-lg leading-relaxed mb-6">
              Enter the approximate width and height of each window in metres to
              get a guide price. Our rates are all inclusive — supply and
              professional fitting with no hidden extras.
            </p>
            <ul className="space-y-2 text-[#7B8A94] text-sm">
              {[
                'Supply & professional fitting included',
                'UK-manufactured uPVC shutters — supply & professional fitting included',
                'Exact pricing confirmed after your free home survey',
                'No obligation — just an honest guide price',
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0F1C2E] flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Right — calculator */}
          <div
            ref={rightRef}
            className="bg-white rounded-2xl border border-[#E8DED1] shadow-sm p-8 reveal-hidden-right"
          >
            <p className="font-display text-[#0F1C2E] text-lg font-medium mb-6">
              Your Windows
            </p>

            {/* Window rows */}
            <div className="space-y-3 mb-5">
              {windows.map((w, idx) => (
                <div key={w.id} className="flex items-center gap-3">
                  <span className="text-[#7B8A94] text-xs w-20 flex-shrink-0">
                    Window {idx + 1}
                  </span>
                  <div className="flex items-center gap-2 flex-1">
                    {/* Width */}
                    <div className="relative flex-1">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Width"
                        value={w.width}
                        onChange={(e) => updateWindow(w.id, 'width', e.target.value)}
                        aria-label={`Window ${idx + 1} width in metres`}
                        className="w-full border border-[#E8DED1] rounded-lg px-3 py-2.5 text-sm text-[#0F1C2E] focus:outline-none focus:border-[#0F1C2E] transition-colors pr-7"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#7B8A94]">
                        m
                      </span>
                    </div>
                    <span className="text-[#7B8A94] text-sm">&times;</span>
                    {/* Height */}
                    <div className="relative flex-1">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Height"
                        value={w.height}
                        onChange={(e) => updateWindow(w.id, 'height', e.target.value)}
                        aria-label={`Window ${idx + 1} height in metres`}
                        className="w-full border border-[#E8DED1] rounded-lg px-3 py-2.5 text-sm text-[#0F1C2E] focus:outline-none focus:border-[#0F1C2E] transition-colors pr-7"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#7B8A94]">
                        m
                      </span>
                    </div>
                    {/* Remove */}
                    {windows.length > 1 && (
                      <button
                        onClick={() => removeWindow(w.id)}
                        className="text-[#7B8A94] hover:text-red-500 transition-colors flex-shrink-0"
                        aria-label={`Remove window ${idx + 1}`}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Add window */}
            <button
              onClick={addWindow}
              className="flex items-center gap-2 text-[#7B8A94] text-sm hover:text-[#0F1C2E] transition-colors mb-6"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Add another window
            </button>

            {/* Error */}
            {error && (
              <p className="text-sm text-red-600 mb-4 flex items-center gap-2" role="alert">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4M12 16h.01" />
                </svg>
                {error}
              </p>
            )}

            {/* Calculate button */}
            <button
              onClick={calculate}
              disabled={!hasValues}
              className="w-full bg-[#0F1C2E] text-white font-medium py-3.5 rounded-full flex items-center justify-center gap-2 hover:bg-[#1B3A5C] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="4" y="2" width="16" height="20" rx="2" />
                <path d="M8 6h8M16 10h.01M12 10h.01M8 10h.01M12 14h.01M8 14h.01M12 18h.01M8 18h.01" />
                <line x1="16" y1="14" x2="16" y2="18" />
              </svg>
              Calculate Guide Price
            </button>

            {/* Result */}
            {result && (
              <div className="mt-6 p-6 bg-[#0F1C2E] rounded-2xl text-white">
                <p className="text-[#E8DED1]/60 text-xs uppercase tracking-widest mb-1">
                  Total Area
                </p>
                <p className="font-display text-3xl font-medium mb-4">
                  {result.sqm} m&sup2;
                </p>
                <div className="border-t border-white/10 pt-4">
                  <p className="text-[#E8DED1]/60 text-xs uppercase tracking-widest mb-1">
                    Guide Price
                  </p>
                  <p className="font-display text-4xl font-medium text-white">
                    &pound;{result.estimate.toLocaleString('en-GB')}
                  </p>
                  <p className="text-[#E8DED1]/50 text-xs mt-2">
                    Guide price — supply &amp; fitting included
                  </p>
                </div>
                <div className="mt-5 flex gap-3">
                  <a
                    href="#contact"
                    className="flex-1 bg-white text-[#0F1C2E] font-medium py-3 rounded-full text-sm text-center hover:bg-[#E8DED1] transition-colors"
                  >
                    Get Exact Quote
                  </a>
                  <button
                    onClick={reset}
                    className="px-4 py-3 rounded-full border border-white/20 text-white/70 text-sm hover:bg-white/10 transition-colors"
                  >
                    Reset
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}